import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, CalendarDays } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { API_URL } from "../data/content";
import { useLang } from "../i18n";

export default function Blog() {
  const { t, lang } = useLang();
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/blog`).then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);

  const pick = (p, f) => (lang === "es" && p[`${f}_es`]) || p[f];
  const fmt = (d) => new Date(d + "T12:00:00").toLocaleDateString(lang === "es" ? "es-ES" : "en-US", { month: "long", day: "numeric", year: "numeric" });

  return (
    <>
      <section className="pt-44 pb-16 bg-cream grain relative overflow-hidden">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-sage blur-3xl" />
        <div className="absolute top-24 right-1/4 w-40 h-40 rounded-full bg-tangerine/10 blur-2xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-tangerine">{t("blog_page_overline")}</p>
            <h1 className="mt-4 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-forest tracking-tight max-w-3xl leading-tight">
              {t("blog_h2")}
            </h1>
            <p className="mt-6 text-bark text-base sm:text-lg max-w-2xl leading-relaxed">{t("blog_page_sub")}</p>
          </Reveal>
        </div>
      </section>

      <section data-testid="blog-list-container" className="py-16 md:py-24 bg-white min-h-[40vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          {posts === null ? (
            <p className="text-bark">{t("blog_loading")}</p>
          ) : posts.length === 0 ? (
            <p className="text-bark">{t("blog_empty")}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
              {posts.map((p, i) => (
                <Reveal key={p.slug} delay={i * 0.07}>
                  <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
                        className="group grid grid-cols-1 sm:grid-cols-5 rounded-3xl overflow-hidden border border-bone bg-cream hover:shadow-2xl hover:shadow-forest/10 transition-shadow duration-500">
                    <div className="sm:col-span-2 h-52 sm:h-full overflow-hidden">
                      <img src={p.cover} alt={pick(p, "title")} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <div className="sm:col-span-3 p-7">
                      <div className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-widest">
                        <span className="text-tangerine">{pick(p, "tag")}</span>
                        <span className="text-bark/50 flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {fmt(p.date)}</span>
                      </div>
                      <h2 className="mt-3 font-display font-bold text-xl text-forest leading-snug group-hover:text-tree transition-colors">{pick(p, "title")}</h2>
                      <p className="mt-3 text-sm text-bark leading-relaxed line-clamp-3">{pick(p, "excerpt")}</p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-tree group-hover:text-tangerine transition-colors">
                        {t("blog_read")} · {lang === "es" ? p.read_time.replace("min read", "min de lectura") : p.read_time} <ArrowUpRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
